'use strict';

const MAP_INSTANCE_KEY = 'addressMapInstance';
const DEFAULT_ZOOM = 16;
const MARKER_ICON_SIZE = 40;
const MARKER_DISPLAY_SIZE = 32;
const COLLAPSED_CLASS = 'address-map--collapsed';
const ANIMATING_CLASS = 'address-map--animating';

let authFailureDetected = false;
let authFailureHookInstalled = false;

/**
 * Parses a coordinate value into a finite number.
 * @param {number|string} value Raw coordinate value
 * @returns {number|null} Parsed coordinate, or null when it is not valid
 */
const parseCoordinate = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const coordinate = parseFloat(value);

    return Number.isFinite(coordinate) ? coordinate : null;
};

/**
 * Indicates whether the Google Maps API is available to render the map.
 * @returns {boolean} True when the required Google Maps APIs are present
 */
const isGoogleMapsAvailable = () => typeof google !== 'undefined'
    && google.maps
    && typeof google.maps.Map === 'function'
    && typeof google.maps.Marker === 'function'
    && typeof google.maps.LatLng === 'function';

/**
 * Indicates whether the coordinates fall inside valid geographic ranges.
 * @param {number} lat Latitude
 * @param {number} lng Longitude
 * @returns {boolean} True when both coordinates are within range
 */
const isLatLngInRange = (lat, lng) => lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

/**
 * Finds the map container associated with the given form.
 * @param {jQuery} $form Form element that hosts the address fields
 * @returns {jQuery|null} The first map container found, or null
 */
const getContainer = ($form) => {
    if (!$form || !$form.length) {
        return null;
    }

    const $container = $form.find('[data-address-map]').first();

    return $container.length ? $container : null;
};

/**
 * Creates the map and its read-only marker for the given container.
 * @param {jQuery} $container Map container
 * @param {google.maps.LatLng} latlng Initial coordinates
 * @returns {Object} The created { map, marker } instance
 */
const createInstance = ($container, latlng) => {
    const map = new google.maps.Map($container.get(0), {
        zoom: DEFAULT_ZOOM,
        center: latlng,
        draggable: true,
        scrollwheel: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false
    });

    const markerOptions = {
        position: latlng,
        map: map,
        draggable: false
    };

    const markerIcon = $container.data('map-marker-icon');

    if (markerIcon) {
        markerOptions.icon = {
            url: markerIcon,
            size: new google.maps.Size(MARKER_ICON_SIZE, MARKER_ICON_SIZE),
            scaledSize: new google.maps.Size(MARKER_DISPLAY_SIZE, MARKER_DISPLAY_SIZE),
            anchor: new google.maps.Point(MARKER_DISPLAY_SIZE / 2, MARKER_DISPLAY_SIZE / 2)
        };
    }

    const instance = {
        map: map,
        marker: new google.maps.Marker(markerOptions)
    };

    $container.data(MAP_INSTANCE_KEY, instance);

    return instance;
};

/**
 * Moves the marker and recenters the map on the given coordinates.
 * @param {Object} instance { map, marker } instance
 * @param {google.maps.LatLng} latlng New coordinates
 * @returns {void}
 */
const updateInstance = (instance, latlng) => {
    instance.marker.setPosition(latlng);
    instance.map.setCenter(latlng);
    instance.map.setZoom(DEFAULT_ZOOM);
};

/**
 * Shows (and initializes when required) the read-only map for a form. Fails
 * silently when the container, the coordinates or Google Maps are missing.
 * @param {jQuery} $form Form element that hosts the address fields
 * @param {number|string} latitude Address latitude
 * @param {number|string} longitude Address longitude
 * @returns {void}
 */
const show = ($form, latitude, longitude) => {
    if (authFailureDetected) {
        return;
    }

    const $container = getContainer($form);
    const lat = parseCoordinate(latitude);
    const lng = parseCoordinate(longitude);

    if (!$container || lat === null || lng === null || !isLatLngInRange(lat, lng) || !isGoogleMapsAvailable()) {
        return;
    }

    const latlng = new google.maps.LatLng(lat, lng);

    try {
        // Reveal instantly (no transition) so the map is measured at full height.
        $container.removeClass('d-none');
        $container.removeClass(ANIMATING_CLASS);
        $container.removeClass(COLLAPSED_CLASS);

        const instance = $container.data(MAP_INSTANCE_KEY) || createInstance($container, latlng);

        updateInstance(instance, latlng);
        google.maps.event.trigger(instance.map, 'resize');
        instance.map.setCenter(latlng);
    } catch (error) {
        // Visual-only component: degrade silently instead of breaking the surrounding form.
        $container.addClass('d-none');
    }
};

/**
 * Hides the map for a form without touching any address field. A previously
 * visible map collapses smoothly; a never-shown map is hidden instantly.
 * @param {jQuery} $form Form element that hosts the address fields
 * @returns {void}
 */
const hide = ($form) => {
    const $container = getContainer($form);

    if (!$container) {
        return;
    }

    const isVisible = !$container.hasClass('d-none') && !$container.hasClass(COLLAPSED_CLASS);

    if (!isVisible) {
        // Never shown or already hidden: hide instantly, without animating.
        $container.addClass('d-none');
        $container.removeClass(ANIMATING_CLASS);
        $container.removeClass(COLLAPSED_CLASS);

        return;
    }

    // Previously visible map: collapse its height smoothly, margin included.
    $container.addClass(ANIMATING_CLASS);
    $container.addClass(COLLAPSED_CLASS);
};

/**
 * Hides every map container rendered by this component (Google Maps
 * authentication failure); the address form keeps working untouched.
 * @returns {void}
 */
const hideAll = () => {
    $('[data-address-map]')
        .addClass('d-none')
        .removeClass(ANIMATING_CLASS)
        .removeClass(COLLAPSED_CLASS);
};

/**
 * Shows the map for a Google Places selection and keeps it hidden when the
 * selection carries no valid geometry (avoids pinning previous coordinates).
 * @param {jQuery} $form Form element that hosts the address fields
 * @param {Object} place Google Places result
 * @returns {void}
 */
const showFromPlace = ($form, place) => {
    const location = place && place.geometry && place.geometry.location;

    if (location && typeof location.lat === 'function' && typeof location.lng === 'function') {
        show($form, location.lat(), location.lng());
    } else {
        hide($form);
    }
};

/**
 * Shows the map using the coordinates rendered on the container data attributes.
 * Stays hidden when no valid coordinates are available.
 * @param {jQuery} $form Form element that hosts the address fields
 * @returns {void}
 */
const showFromContainer = ($form) => {
    const $container = getContainer($form);

    if ($container) {
        show($form, $container.data('latitude'), $container.data('longitude'));
    }
};

/**
 * Installs the documented Google Maps authentication-failure hook
 * (window.gm_authFailure) once, preserving any pre-existing handler.
 * @returns {void}
 */
const installAuthFailureHook = () => {
    if (authFailureHookInstalled) {
        return;
    }

    authFailureHookInstalled = true;

    const previousAuthFailure = window.gm_authFailure;

    window.gm_authFailure = () => {
        authFailureDetected = true;
        hideAll();

        if (typeof previousAuthFailure === 'function') {
            previousAuthFailure();
        }
    };
};

installAuthFailureHook();

module.exports = {
    show: show,
    hide: hide,
    showFromPlace: showFromPlace,
    showFromContainer: showFromContainer
};
